import { useEffect, useRef, useState } from 'react'

// WatchingNowIcon — the glyph for "I'm watching this right now" (2026-09-19, Jason:
// "a new yet related icon like the moon and clapboard which animates on tap"). Same
// "Solid Facet" language as MoonIcon/FilmReelIcon/ClapboardIcon: one --ic color-mix
// base, a filled glyph, a burst on activation. Concept is a play mark inside a ring —
// reads as LIVE/ongoing (repeating pulse while active) rather than a completed action
// like the clapboard's single snap, which is the right feeling for a session that's
// still open.
//
// `active` mirrors FilmReel/Moon's pattern: false→true fires one pop + two outward
// rings. Pass `live` (distinct from `active`) to keep a slow ambient pulse going for
// as long as the session stays open — used where the icon needs to read as "still
// watching," not just "just tapped."
export function WatchingNowIcon({ size = 24, color = 'currentColor', active, live = false }) {
  const [burst, setBurst] = useState(false)
  const prev = useRef(active)

  useEffect(() => {
    if (active && !prev.current) {
      setBurst(true)
      const t = setTimeout(() => setBurst(false), 700)
      prev.current = active
      return () => clearTimeout(t)
    }
    prev.current = active
  }, [active])

  // wnRingPulse/wnPop keyframes live in index.css, not an inline <style> here — see
  // MoonIcon's comment: a <style> child of <svg> isn't reliably parsed as a
  // stylesheet on every browser, and renders as literal text on the ones where it
  // isn't (found live via friction telemetry).
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ overflow: 'visible', '--ic': color }}>
      {(burst || live) && (
        <circle cx="12" cy="12" r="9" fill="none" stroke={color} strokeWidth="1.5" opacity="0"
          style={{ transformBox: 'view-box', transformOrigin: '12px 12px', '--nxa': `wnRingPulse ${live ? '1.8s' : '0.7s'} ease-out ${live ? 'infinite' : 'both'}` }} />
      )}
      {burst && (
        <circle cx="12" cy="12" r="9" fill="none" stroke={color} strokeWidth="1.5" opacity="0"
          style={{ transformBox: 'view-box', transformOrigin: '12px 12px', '--nxa': 'wnRingPulse 0.7s ease-out 0.18s both' }} />
      )}
      <g style={{
        transformBox: 'view-box', transformOrigin: '12px 12px',
        '--nxa': burst ? 'wnPop 0.55s var(--ease-pop, cubic-bezier(0.34,1.56,0.64,1))' : 'none',
      }}>
        <circle cx="12" cy="12" r="8" fill="none" stroke="var(--ic)" strokeWidth="1.6"/>
        <path d="M10 8.3 L16 12 L10 15.7 Z" fill="color-mix(in srgb, var(--ic) 88%, white 12%)"/>
      </g>
    </svg>
  )
}
